import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Filter,
  Code,
  CheckCircle2,
  Clock,
  Tag,
  ChevronDown,
  X,
  AlertCircle,
  Info,
  Plus,
  Users
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import CreateCodingChallengeModal from './CreateCodingChallengeModal'

interface CodingProblem {
  id: string
  title: string
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  category: string
  tags: string[]
  time_limit: number
  allowed_languages: string[]
  examples: any[]
  is_active: boolean
  created_by?: number
}

interface CodingProblemsSelectorProps {
  selectedProblems: string[]
  onProblemsChange: (problemIds: string[]) => void
  maxProblems?: number
  allowedDifficulties?: string[]
  className?: string
  categoryFilter?: string
  allowedCategories?: string[]
}

export default function CodingProblemsSelector({
  selectedProblems,
  onProblemsChange,
  maxProblems = 5,
  allowedDifficulties = ['easy', 'medium', 'hard'],
  className = '',
  categoryFilter,
  allowedCategories
}: CodingProblemsSelectorProps) {
  const [problems, setProblems] = useState<CodingProblem[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [teamMembers, setTeamMembers] = useState<number[]>([])
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showTeamProblemsOnly, setShowTeamProblemsOnly] = useState(false)
  
  useEffect(() => {
    loadProblemsAndCategories()
  }, [selectedDifficulty, selectedCategory, searchQuery, showTeamProblemsOnly])

  useEffect(() => {
    // Load current user and team members
    loadUserAndTeamData()
  }, [])

  const loadUserAndTeamData = async () => {
    try {
      // Get current user from localStorage
      if (typeof window !== 'undefined') {
        const userData = localStorage.getItem('currentUser')
        if (userData) {
          const user = JSON.parse(userData)
          setCurrentUser(user)
          
          // Load team members if we have a user
          if (user.id) {
            await loadTeamMembers(user.id)
          }
        }
      }
    } catch (error) {
      console.error('Failed to load user data:', error)
    }
  }

  const loadTeamMembers = async (userId: number) => {
    try {
      // Get users who are in the same team as current user
      const usersResponse = await apiClient.getUsers({ 
        sameTeam: true,
        isActive: true
      })
      
      if (usersResponse.success && usersResponse.data) {
        const users = Array.isArray(usersResponse.data) 
          ? usersResponse.data 
          : (usersResponse.data as any)?.users || []
        
        // Extract user IDs including current user
        const memberIds = users
          .map((user: any) => user.id)
          .filter((id: number) => id && typeof id === 'number')
        
        setTeamMembers(memberIds)
      } else {
        // Fallback: just include current user
        setTeamMembers([userId])
      }
    } catch (error) {
      console.error('Failed to load team members:', error)
      // Fallback: just include current user
      setTeamMembers([userId])
    }
  }

  const loadProblemsAndCategories = async () => {
    try {
      setLoading(true)
      
      // Determine which API to call based on categoryFilter
      const isDatabaseQuestions = categoryFilter === 'Database'

      const [problemsResponse, categoriesResponse] = await Promise.all([
        isDatabaseQuestions 
          ? apiClient.getDatabaseChallenges({
              difficulty: selectedDifficulty || undefined,
              challenge_type: selectedCategory || undefined,
              limit: 50
            })
          : apiClient.getCodingProblems({
              difficulty: selectedDifficulty || undefined,
              category: selectedCategory || categoryFilter || undefined,
              search: searchQuery || undefined,
              limit: 50
            }),
        apiClient.getCodingProblemCategories()
      ])

      if (problemsResponse.success) {
        let problems = []
        
        if (isDatabaseQuestions) {
          // Database challenges API returns data directly as array
          problems = Array.isArray(problemsResponse.data) ? problemsResponse.data : []
          
          // Convert database challenges to match the expected format
          problems = problems.map(challenge => ({
            id: challenge.uuid,
            title: challenge.title,
            description: challenge.description,
            difficulty: challenge.difficulty?.toLowerCase() || 'medium',
            category: 'Database',
            tags: [],
            time_limit: challenge.time_limit_minutes || 60,
            allowed_languages: ['sql'],
            examples: [],
            is_active: true,
            created_by: challenge.created_by_user_id
          }))
        } else {
          // Coding problems API returns data.problems
          problems = Array.isArray((problemsResponse.data as any)?.problems) ? (problemsResponse.data as any).problems.map((problem: any) => ({
            ...problem,
            id: problem.uuid
          })) : []
        }
        
        setProblems(problems)
      } else {
        setProblems([])
        setError(isDatabaseQuestions ? 'Failed to load database questions' : 'Failed to load coding problems')
      }

      if (categoriesResponse.success && categoriesResponse.data?.categories) {
        setCategories(Array.isArray(categoriesResponse.data.categories) ? categoriesResponse.data.categories : [])
      } else {
        setCategories([])
      }
    } catch (err) {
      const errorMessage = categoryFilter === 'Database' ? 'Failed to load database questions' : 'Failed to load coding problems'
      setError(errorMessage)
      console.error('Error loading problems:', err)
      // Ensure arrays are initialized even on error
      setProblems([])
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const toggleProblem = (problemId: string) => {
    if (selectedProblems.includes(problemId)) {
      onProblemsChange(selectedProblems.filter(id => id !== problemId))
    } else if (selectedProblems.length < maxProblems) {
      onProblemsChange([...selectedProblems, problemId])
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedDifficulty('')
    setSelectedCategory('')
    setShowTeamProblemsOnly(false)
  }

  const handleProblemCreated = (newProblem?: any) => {
    setShowCreateModal(false)
    
    // If we have the new problem data and can add more problems, auto-select it
    if (newProblem && selectedProblems.length < maxProblems) {
      onProblemsChange([...selectedProblems, newProblem.uuid])
    }
    
    // Reload problems to include the newly created one
    loadProblemsAndCategories()
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hard': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const selectedProblemsData = (problems || []).filter(p => selectedProblems.includes(p.uuid))
  const totalTimeEstimate = selectedProblemsData.reduce((sum, p) => sum + p.time_limit, 0)

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="border rounded-lg p-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Problems</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadProblemsAndCategories}
              className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {categoryFilter === 'Database' ? 'Select Database Questions' : 'Select Coding Problems'}
          </h3>
          <p className="text-sm text-gray-500">
            Choose up to {maxProblems} {categoryFilter === 'Database' ? 'database questions' : 'problems'} for this technical interview
            {categoryFilter && (
              <span className="text-blue-600 font-medium"> • {categoryFilter} category</span>
            )}
            {showTeamProblemsOnly && (
              <span className="text-purple-600 font-medium"> • Showing team problems only</span>
            )}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Challenge</span>
          </button>
          
          <button
            onClick={() => setShowTeamProblemsOnly(!showTeamProblemsOnly)}
            className={`flex items-center space-x-2 px-3 py-1.5 text-sm border rounded-md transition-colors ${
              showTeamProblemsOnly
                ? 'text-white bg-purple-600 border-purple-600 hover:bg-purple-700'
                : 'text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Team Problems</span>
          </button>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Selected Problems Summary */}
      {selectedProblems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-900">
              Selected Problems ({selectedProblems.length}/{maxProblems})
            </h4>
            <div className="text-sm text-blue-700">
              Estimated time: {totalTimeEstimate} minutes
            </div>
          </div>
          <div className="space-y-2">
            {selectedProblemsData.map(problem => (
              <div key={problem.uuid} className="flex items-center justify-between bg-white rounded px-3 py-2">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                    {problem.difficulty.toUpperCase()}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{problem.title}</span>
                  <span className="text-xs text-gray-500">{problem.category}</span>
                </div>
                <button
                  onClick={() => toggleProblem(problem.uuid)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4"
          >
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Problems</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title or description..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Difficulty Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Difficulties</option>
                  {allowedDifficulties.map(diff => (
                    <option key={diff} value={diff}>
                      {diff.charAt(0).toUpperCase() + diff.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {(categories || []).map(cat => (
                    <option key={cat.category} value={cat.category}>
                      {cat.category} ({cat.problem_count})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(searchQuery || selectedDifficulty || selectedCategory || showTeamProblemsOnly) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-500 underline"
              >
                Clear all filters
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Problems List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {(() => {
          // Filter problems based on team filter and allowed categories
          let filteredProblems = problems || []
          
          // Filter by allowed categories if specified
          if (allowedCategories && allowedCategories.length > 0) {
            filteredProblems = filteredProblems.filter(problem => 
              allowedCategories.includes(problem.category)
            )
          }
          
          if (showTeamProblemsOnly && teamMembers.length > 0) {
            filteredProblems = filteredProblems.filter(problem => 
              problem.created_by && teamMembers.includes(problem.created_by)
            )
          }
          
          if (filteredProblems.length === 0) {
            return (
              <div className="text-center py-8 text-gray-500">
                <Code className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>
                  {showTeamProblemsOnly 
                    ? 'No problems found from your team members' 
                    : 'No problems found matching your criteria'
                  }
                </p>
                {showTeamProblemsOnly && teamMembers.length === 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    You may not be part of any teams yet
                  </p>
                )}
              </div>
            )
          }
          
          return filteredProblems.map(problem => {
            const isSelected = selectedProblems.includes(problem.uuid)
            const canSelect = isSelected || selectedProblems.length < maxProblems
            
            return (
              <div
                key={problem.uuid}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : canSelect
                    ? 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                }`}
                onClick={() => canSelect && toggleProblem(problem.uuid)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center space-x-2">
                        {isSelected && <CheckCircle2 className="w-4 h-4 text-blue-600" />}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                          {problem.difficulty.toUpperCase()}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900">{problem.title}</h4>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {problem.description}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Tag className="w-3 h-3" />
                        <span>{problem.category}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{problem.time_limit} min</span>
                      </div>
                      {problem.tags && problem.tags.length > 0 && (
                        <div className="flex space-x-1">
                          {problem.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="bg-gray-100 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                          {problem.tags.length > 2 && (
                            <span className="text-gray-400">+{problem.tags.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        })()}
      </div>

      {/* Help Text */}
      {selectedProblems.length === maxProblems && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
          <Info className="w-4 h-4 text-amber-600 mt-0.5" />
          <p className="text-sm text-amber-800">
            You've reached the maximum of {maxProblems} problems. Remove a problem to select another.
          </p>
        </div>
      )}

      {/* Create Coding Challenge Modal */}
      <CreateCodingChallengeModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProblemCreated={handleProblemCreated}
      />
    </div>
  )
}